import { motion } from "framer-motion";
import { Bot, Workflow, FileSearch, LineChart, Plug, Radar } from "lucide-react";

const SERVICES = [
  {
    icon: Workflow,
    title: "AI Workflow Automation",
    desc: "End-to-end automation of repetitive operations. We map your processes, then deploy intelligent pipelines that execute them flawlessly — 24/7, without fatigue.",
    span: "lg:col-span-4 lg:row-span-2",
    image: "https://images.unsplash.com/photo-1567095751004-aa51a2690368?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBvY2VhbiUyMHdhdmVzfGVufDB8fHx8MTc4NjE0NDgzN3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Bot,
    title: "Conversational AI Agents",
    desc: "Custom agents that handle support, sales and onboarding in your brand voice.",
    span: "lg:col-span-2",
    image: "https://images.unsplash.com/photo-1760344594784-60ff14035eb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHx0ZWNoJTIwYWJzdHJhY3QlMjBnbG93aW5nJTIwZ3JlZW58ZW58MHx8fHwxNzg2MTQ0ODM3fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: FileSearch,
    title: "Document Intelligence",
    desc: "Extract, classify and route data from invoices, contracts and forms automatically.",
    span: "lg:col-span-2",
  },
  {
    icon: LineChart,
    title: "Predictive Analytics",
    desc: "Forecast demand, churn and revenue with models trained on your own data.",
    span: "lg:col-span-2",
  },
  {
    icon: Plug,
    title: "AI Integrations & APIs",
    desc: "We wire GPT-class models into your stack — CRM, ERP, support desk and beyond.",
    span: "lg:col-span-2",
  },
  {
    icon: Radar,
    title: "Automation Audit",
    desc: "A deep scan of your operations that surfaces the highest-ROI automation targets.",
    span: "lg:col-span-2",
  },
];

export const Services = () => {
  return (
    <section id="services" className="relative py-28 lg:py-36 px-6" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-16 lg:mb-20 max-w-2xl"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-[#10E59A] mb-5">Chapter 01 — What we build</p>
          <h2 className="font-['Outfit'] font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#E8F1F5]">
            Systems that <span className="gradient-text font-medium">think & act</span> for you
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-5 lg:auto-rows-[190px]">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              data-testid={`service-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              whileHover={{ y: -6 }}
              className={`group relative overflow-hidden rounded-2xl border border-[#00F2FE]/15 bg-[#051221]/70 backdrop-blur-sm p-7 flex flex-col justify-end min-h-[220px] hover:border-[#00F2FE]/45 hover:shadow-[0_0_35px_-10px_rgba(0,242,254,0.35)] transition-[border-color,box-shadow] duration-500 ${s.span}`}
            >
              {s.image && (
                <>
                  <img
                    src={s.image}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-40 group-hover:scale-105 transition-[opacity,transform] duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020813] via-[#020813]/60 to-transparent" />
                </>
              )}
              <div className="relative z-10">
                <s.icon className="w-7 h-7 text-[#00F2FE] mb-4 group-hover:text-[#10E59A] transition-colors duration-300" />
                <h3 className="font-['Outfit'] font-medium text-lg text-[#E8F1F5] mb-2">{s.title}</h3>
                <p className="text-sm leading-relaxed text-[#8AA1B1]">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
